import { isCardLikePayment } from '@/utils/saleDisplay'

/**
 * @param {object|null|undefined} pt
 * @returns {Record<string, unknown>}
 */
export function parsePaymentTransactionRaw (pt) {
  const raw = pt?.response_payload
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  }
  return {}
}

/**
 * @param {object|null|undefined} payment
 * @returns {boolean}
 */
export function isCashTenderPayment (payment) {
  if (!payment) return false
  if (payment.payment_method === 'cash') return true
  const type = String(payment.payment_method_type || '').toLowerCase()
  return payment.payment_method === 'split' && type === 'cash'
}

/**
 * @param {object|null|undefined} pt
 * @returns {boolean}
 */
export function isApprovedCardTransaction (pt) {
  const status = String(pt?.status ?? '').toLowerCase()
  return status === 'approved' || status === 'settled' || status === 'succeeded'
}

/**
 * @param {object|null|undefined} pt
 * @param {Record<string, unknown>} raw
 * @returns {string|null}
 */
export function resolveThermalCardRef (pt, raw) {
  const rrn = raw?.rrn
  if (rrn != null && String(rrn).trim()) {
    return String(rrn).trim()
  }
  const resultCode = pt?.result_code
  if (resultCode != null && String(resultCode).trim()) {
    return String(resultCode).trim()
  }
  const reference = pt?.reference
  if (reference != null && String(reference).trim()) {
    return String(reference).trim()
  }
  return null
}

/**
 * @param {string|null|undefined} brand
 * @returns {string}
 */
export function formatThermalCardBrand (brand) {
  const b = String(brand ?? '').trim()
  if (!b) return 'Card'
  const lower = b.toLowerCase()
  const known = {
    amex: 'AMEX',
    visa: 'VISA',
    mastercard: 'MASTERCARD',
    mc: 'MC',
    discover: 'DISCOVER',
    diners: 'DINERS',
    jcb: 'JCB'
  }
  return known[lower] || b.toUpperCase()
}

/**
 * @param {string|null|undefined} entryMode
 * @param {Record<string, string>} L
 * @returns {string}
 */
export function formatThermalEntryMode (entryMode, L = {}) {
  const mode = String(entryMode ?? '').toLowerCase()
  const labels = {
    chip: L.entryModeChip || 'Chip',
    contactless: L.entryModeContactless || 'Contactless',
    swipe: L.entryModeSwipe || 'Swipe',
    manual: L.entryModeManual || 'Manual',
    cnp: L.entryModeKeyed || 'Keyed',
    unknown: L.entryModeUnknown || 'Card'
  }
  if (labels[mode]) {
    return labels[mode]
  }
  if (!mode) return ''
  return mode.charAt(0).toUpperCase() + mode.slice(1)
}

/**
 * @param {object|null|undefined} pt
 * @param {Record<string, unknown>} raw
 * @param {Record<string, string>} L
 * @returns {string|null}
 */
export function buildThermalCardIdentityLine (pt, raw, L = {}) {
  const brand = formatThermalCardBrand(pt?.card_brand ?? raw?.cardLabel ?? raw?.label)
  const maskedPan = raw?.maskedPan != null ? String(raw.maskedPan) : ''
  const last4 = pt?.card_last4 ?? pt?.last4 ?? (maskedPan ? maskedPan.slice(-4) : null)
  const entry = formatThermalEntryMode(pt?.entry_mode, L)
  const parts = [brand]
  if (entry) parts.push(entry)
  if (last4) parts.push(`****${last4}`)
  if (parts.length === 1 && parts[0] === 'Card') {
    return null
  }
  return parts.join(' · ')
}

/**
 * @param {object|null|undefined} pt
 * @param {Record<string, unknown>} raw
 * @param {Record<string, string>} L
 * @returns {string|null}
 */
export function buildThermalCardApprovalLine (pt, raw, L = {}) {
  const status = String(pt?.status ?? '').toLowerCase()
  if (status === 'declined' || status === 'error') {
    return L.paymentDeclined || 'DECLINED'
  }
  if (!isApprovedCardTransaction(pt)) {
    return null
  }
  const auth = pt?.auth_code ?? raw?.approvalCode ?? raw?.responseApprovalCode
  if (auth != null && String(auth).trim()) {
    const authPrefix = L.authShort || 'Auth'
    return `${L.paymentApproved || 'APPROVED'} · ${authPrefix} ${String(auth).trim()}`
  }
  return L.paymentApproved || 'APPROVED'
}

/**
 * @param {object|null|undefined} pt
 * @param {Record<string, unknown>} raw
 * @param {Record<string, string>} L
 * @returns {string|null}
 */
export function buildThermalCardRefLine (pt, raw, L = {}) {
  const ref = resolveThermalCardRef(pt, raw)
  if (!ref) return null
  return `${L.refShort || 'Ref'} ${ref}`
}

/**
 * @param {string|null|undefined} entryMode
 * @returns {boolean}
 */
export function shouldShowThermalEmvLine (entryMode) {
  const mode = String(entryMode ?? '').toLowerCase()
  return mode === 'chip' || mode === 'contactless'
}

/**
 * @param {object|null|undefined} pt
 * @param {Record<string, unknown>} raw
 * @returns {string|null}
 */
export function buildThermalCardEmvLine (pt, raw) {
  if (!shouldShowThermalEmvLine(pt?.entry_mode)) {
    return null
  }
  const aid = pt?.emv_aid ?? raw?.emvAID ?? raw?.aid
  const appName = raw?.emvAppName ?? raw?.appName
  const aidStr = aid != null ? String(aid).trim() : ''
  const appStr = appName != null ? String(appName).trim() : ''
  if (appStr && aidStr) {
    return `${appStr} · ${aidStr}`
  }
  if (aidStr) {
    return `AID ${aidStr}`
  }
  if (appStr) {
    return appStr
  }
  return null
}

/**
 * Compact card compliance lines for thermal receipts.
 * @param {object|null|undefined} payment
 * @param {Record<string, string>} [L]
 * @returns {string[]}
 */
export function buildThermalCardPaymentDetailLines (payment, L = {}) {
  const pt = payment?.payment_transaction
  if (!pt || !isCardLikePayment(payment) || isCashTenderPayment(payment)) {
    return []
  }
  const raw = parsePaymentTransactionRaw(pt)
  return [
    buildThermalCardIdentityLine(pt, raw, L),
    buildThermalCardApprovalLine(pt, raw, L),
    buildThermalCardRefLine(pt, raw, L),
    buildThermalCardEmvLine(pt, raw)
  ].filter(Boolean)
}

/**
 * @param {(text: string) => void} addLines
 * @param {object|null|undefined} payment
 * @param {Record<string, string>} [L]
 */
export function appendThermalCardPaymentDetails (addLines, payment, L = {}) {
  for (const line of buildThermalCardPaymentDetailLines(payment, L)) {
    addLines(line)
  }
}
